import {MutableRefObject, useContext, useEffect, useRef, useState} from 'react';
import {useHistory, useLocation, useRouteMatch} from 'react-router-dom';
import {AuthContext} from '../contexts/auth/context';
import {AuthContextType} from '../contexts/auth/types';
import {PaginatedTableData} from '../interfaces/paginated-table-data';

// Hold variables
const PAGE_SIZE: number = 6;

// Get query parameters
function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export function usePaginatedTable<T>(params: {
  fetchData: (
    limit: number,
    skip: number,
    search?: string
  ) => Promise<{
    totalCount: number;
    data: PaginatedTableData<T>;
  }>;
}) {
  // Get context
  const authContext: AuthContextType = useContext(AuthContext);

  // Hold state
  const [data, setData] = useState<PaginatedTableData<T>>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | undefined>();
  const [pagesCount, setPagesCount] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentSearch, setCurrentSearch] = useState<string>('');

  // Get navigation info
  let query = useQuery();
  let history = useHistory();

  // The `path` lets us build <Route> paths that are
  // relative to the parent route, while the `url` lets
  // us build relative links.
  let {url} = useRouteMatch();

  // Hold ref
  const currentUrlRef: MutableRefObject<string | undefined> = useRef(url);
  const mountedRef: MutableRefObject<boolean> = useRef(false);

  // Fetch data on component mount and when the history or the selected affiliation change
  useEffect(() => {
    // Fetch data on component mount
    if (mountedRef.current === false) {
      handleGetData(
        query.get('page') ? parseInt(query.get('page') as string) : undefined,
        query.get('search') ? (query.get('search') as string) : undefined
      );
    }

    // Listen for route changes
    const routeChangesUnsubscribe = history.listen((location: any) => {
      // If we are still here
      if (location.pathname === currentUrlRef.current) {
        // Remap params
        const newQuery = new URLSearchParams(location.search);

        // Update data
        handleGetData(
          newQuery.get('page') ? parseInt(newQuery.get('page') as string) : undefined,
          newQuery.get('search') ? (newQuery.get('search') as string) : undefined
        );
      } else {
        // Update it
        currentUrlRef.current = location.pathname;
      }
    });

    // Set mounted
    mountedRef.current = true;

    // Clean up
    return () => {
      // Unsubscribe
      routeChangesUnsubscribe();

      // Unset mounted
      mountedRef.current = false;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  const handleApplyFilters = (pageNumber?: number, searchString?: string) => {
    // Define route
    let queryTextParts: Array<string> = [];

    // Page
    if (pageNumber) {
      queryTextParts.push(`page=${pageNumber}`);
    } else if (currentPage) {
      queryTextParts.push(`page=${currentPage}`);
    }

    // Search
    if (searchString && searchString.trim().length !== 0) {
      queryTextParts.push(`search=${searchString.trim()}`);
    } else if (currentSearch && currentSearch.trim().length !== 0) {
      queryTextParts.push(`search=${currentSearch.trim()}`);
    }

    // Build new URL
    history.push(`${url}` + (queryTextParts.length > 0 ? `?${queryTextParts.join('&')}` : ''));
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    // Apply page change to filters
    handleApplyFilters(value, undefined);

    // Scroll to top
    window.scrollTo({
      top: window.innerWidth > 600 ? 160 : 230,
      behavior: 'smooth',
    });
  };

  // Methods
  const handleGetData = async (page?: number, search?: string) => {
    // Set loading
    setIsLoading(true);

    // Security check
    if (
      authContext.state.session?.affiliations &&
      authContext.state.session?.affiliations.length > 0 &&
      !authContext.state.session?.selectedAffiliation
    ) {
      // Log
      console.warn('Current user does not have a selected affiliation so it cannot proceed.');

      // Set values to empty
      setData([]);
      setPagesCount(0);
      setCurrentPage(1);
      setError(undefined);

      // Stop loading
      setIsLoading(false);

      // Stop execution here
      return;
    }

    // Clear error
    setError(undefined);

    try {
      // Current filters
      const sanitizedPage: number = page ? page : 1;
      const sanitizedSearch: string = search ? search : '';

      // Update filters values
      setCurrentPage(sanitizedPage);
      setCurrentSearch(sanitizedSearch);

      // Load data
      const result: {totalCount: number; data: PaginatedTableData<T>} = await params.fetchData(
        PAGE_SIZE,
        (sanitizedPage - 1) * PAGE_SIZE,
        search
      );

      // Update data
      setData(result.data);
      setPagesCount(Math.ceil(result.totalCount / PAGE_SIZE));
    } catch (error: any) {
      // Log
      console.error('🔴 Could not get data.', error);

      // Update data
      setData([]);
      setPagesCount(0);
      setCurrentPage(1);

      // Show error
      setError(error.message);
    }

    // Stop loading
    setIsLoading(false);
  };

  // Update the current search
  const handleUpdateCurrentSearch = (search: string) => {
    setCurrentSearch(search);
  };

  // Update an item
  const handleUpdateItem = (
    updatedItemId: string,
    updatedColumns: Array<{
      updatedColumnId: string;
      updatedColumnValue: string;
      updatedColumnAvatarUrl?: string;
    }>,
    updatedItem?: T,
    enabled?: boolean
  ) => {
    // Update item
    setData((prevData) =>
      prevData.map((item) => {
        if (item.id === updatedItemId) {
          return {
            ...item,
            // Update the item
            item: updatedItem || item.item,
            // Update enabled if it's set on the parameters
            enabled: typeof enabled == 'boolean' ? enabled : item.enabled,
            // Update the columns
            columns: item.columns.map((columnValue) => {
              // Find it
              const newColumnValue = updatedColumns.find(
                (obj) => obj.updatedColumnId === columnValue.columnId
              );

              // Set new value
              if (newColumnValue) {
                return {
                  ...columnValue,
                  // Update column value
                  value: newColumnValue.updatedColumnValue,
                  // Update column avatar
                  avatarUrl: newColumnValue.updatedColumnAvatarUrl,
                };
              } else {
                return columnValue;
              }
            }),
          };
        } else {
          return item;
        }
      })
    );
  };

  return {
    data,
    isLoading,
    error,
    pagesCount,
    currentPage,
    currentSearch,
    handlePageChange,
    handleApplyFilters,
    handleUpdateCurrentSearch,
    handleUpdateItem,
  };
}
